import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Send, ShieldCheck } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  { value: "medical", label: "Medical Interpreting" },
  { value: "legal", label: "Legal Interpreting" },
  { value: "immigration", label: "Immigration Interpreting" },
  { value: "business", label: "Business Interpreting" },
  { value: "translation", label: "Document Translation" },
  { value: "other", label: "Other / Not sure yet" },
];

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        service: form.service || null,
        message: form.message.trim(),
      });
      toast.success("Thank you — your message has been sent. Eve will reach out soon.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data?.detail) ? "Please check your inputs." : null) ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please check your inputs.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative">
      <div className="container-edge py-20 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left intro */}
          <div className="lg:col-span-5">
            <div className="eyebrow" data-testid="contact-eyebrow">Get in touch</div>
            <h2
              data-testid="contact-heading"
              className="mt-5 font-display text-[hsl(var(--ink))] text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]"
            >
              Tell me about your
              <span className="italic text-[hsl(var(--accent))]"> assignment.</span>
            </h2>
            <p className="mt-6 text-[hsl(var(--text-muted))] text-base md:text-lg leading-relaxed max-w-md">
              Share a few details and I'll respond personally with availability, rates,
              and the best way to support your client, patient, or team.
            </p>

            <ul className="mt-10 space-y-6">
              <li className="flex items-start gap-4" data-testid="contact-info-email">
                <span className="mt-1 inline-flex items-center justify-center w-10 h-10 border border-[hsl(var(--line))] text-[hsl(var(--accent))]">
                  <Mail size={16} />
                </span>
                <div>
                  <div className="eyebrow !text-[hsl(var(--text-muted))] flex items-center gap-2">
                    Email
                    <span
                      data-testid="contact-email-preferred"
                      className="text-[10px] tracking-[0.18em] px-2 py-0.5 bg-[hsl(var(--accent))] text-white"
                    >
                      Preferred
                    </span>
                  </div>
                  <a
                    href="mailto:eginterpreting@gmail.com"
                    className="mt-1 block font-display text-xl text-[hsl(var(--ink))] hover:text-[hsl(var(--accent))] transition-colors"
                    data-testid="contact-email-link"
                  >
                    eginterpreting@gmail.com
                  </a>
                  <p className="mt-1 text-sm text-[hsl(var(--text-muted))]">
                    Best way to request interpreting jobs and assignments.
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-4" data-testid="contact-info-phone">
                <span className="mt-1 inline-flex items-center justify-center w-10 h-10 border border-[hsl(var(--line))] text-[hsl(var(--accent))]">
                  <Phone size={16} />
                </span>
                <div>
                  <div className="eyebrow !text-[hsl(var(--text-muted))]">Phone</div>
                  <a
                    href="tel:+12192627009"
                    className="mt-1 block font-display text-xl text-[hsl(var(--ink))] hover:text-[hsl(var(--accent))] transition-colors"
                    data-testid="contact-phone-link"
                  >
                    (219) 262-7009
                  </a>
                  <p className="mt-1 text-sm text-[hsl(var(--text-muted))]">
                    For quick questions — for jobs, please email.
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-4" data-testid="contact-info-area">
                <span className="mt-1 inline-flex items-center justify-center w-10 h-10 border border-[hsl(var(--line))] text-[hsl(var(--accent))]">
                  <MapPin size={16} />
                </span>
                <div>
                  <div className="eyebrow !text-[hsl(var(--text-muted))]">Service area</div>
                  <div className="mt-1 font-display text-xl text-[hsl(var(--ink))]">
                    On-site &amp; Remote · Nationwide
                  </div>
                </div>
              </li>

              <li className="flex items-start gap-4" data-testid="contact-info-confidential">
                <span className="mt-1 inline-flex items-center justify-center w-10 h-10 border border-[hsl(var(--line))] text-[hsl(var(--accent))]">
                  <ShieldCheck size={16} />
                </span>
                <div>
                  <div className="eyebrow !text-[hsl(var(--text-muted))]">Confidentiality</div>
                  <div className="mt-1 font-display text-xl text-[hsl(var(--ink))]">
                    Every conversation. Always.
                  </div>
                </div>
              </li>
            </ul>
          </div>

          {/* Right form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            onSubmit={onSubmit}
            noValidate
            data-testid="contact-form"
            className="lg:col-span-7 bg-[hsl(var(--surface))] border border-[hsl(var(--line))] p-8 md:p-12"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <div>
                <label className="eyebrow !text-[hsl(var(--text-muted))]" htmlFor="name">
                  Your name *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={onChange}
                  required
                  className="field-underline mt-2"
                  placeholder="Jane Doe"
                  data-testid="contact-input-name"
                />
              </div>

              <div>
                <label className="eyebrow !text-[hsl(var(--text-muted))]" htmlFor="email">
                  Email *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  className="field-underline mt-2"
                  placeholder="you@company.com"
                  data-testid="contact-input-email"
                />
              </div>

              <div>
                <label className="eyebrow !text-[hsl(var(--text-muted))]" htmlFor="phone">
                  Phone (optional)
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={onChange}
                  className="field-underline mt-2"
                  placeholder="(555) 555-0123"
                  data-testid="contact-input-phone"
                />
              </div>

              <div>
                <label className="eyebrow !text-[hsl(var(--text-muted))]" htmlFor="service">
                  Service needed
                </label>
                <Select
                  value={form.service}
                  onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}
                >
                  <SelectTrigger
                    id="service"
                    data-testid="contact-select-service"
                    className="mt-2 bg-transparent border-0 border-b border-[hsl(var(--line))] rounded-none px-0 h-auto py-3 focus:ring-0 focus:border-[hsl(var(--accent))] text-base text-[hsl(var(--ink))] [&>span]:text-left"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--surface))]">
                    {SERVICES.map((s) => (
                      <SelectItem
                        key={s.value}
                        value={s.value}
                        data-testid={`contact-service-option-${s.value}`}
                      >
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <label className="eyebrow !text-[hsl(var(--text-muted))]" htmlFor="message">
                  How can I help? *
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={onChange}
                  required
                  className="field-underline mt-2 resize-none"
                  placeholder="A few details about the assignment, date, location, and language pair…"
                  data-testid="contact-input-message"
                />
              </div>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <p className="text-xs text-[hsl(var(--text-muted))] max-w-sm leading-relaxed">
                By submitting this form you agree to be contacted about your inquiry.
                Your information stays confidential.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? "Sending…" : (<>Send Message <Send size={15} /></>)}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
